import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Mail, ExternalLink, CheckCircle, AlertCircle } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { SEO } from '../components/SEO';
import { getApiBase } from '../lib/apiBase';


const API_URL = getApiBase();

const ARTICLES = [
  {
    title: 'Low-Carbon Concrete',
    tag: 'Materials \u00B7 Concrete Chronicles',
    excerpt: 'An exploration of how supplementary cementitious materials, specification standards and lifecycle thinking are reshaping sustainable concrete design.',
    href: 'https://www.linkedin.com/pulse/low-carbon-concrete-nuno-filipe-ferreira/',
  },
  {
    title: 'Durability Dollars \u2014 Future-Proofing Concrete Infrastructure',
    tag: 'Materials \u00B7 Concrete Chronicles',
    excerpt: 'The economics of durable concrete design \u2014 why upfront investment in performance-based specification delivers exponential returns over the infrastructure lifecycle.',
    href: 'https://www.linkedin.com/pulse/durability-dollars-part-1-future-proofing-concrete-ferreira-qsnuc/',
  },
];

export const Knowledge = () => {
  const { t } = useTranslation();
  const [articleTab, setArticleTab] = useState('materials');
  const [subEmail, setSubEmail] = useState('');
  const [subStatus, setSubStatus] = useState('idle'); // idle | submitting | success | already | error

  return (
    <div className="min-h-screen pt-16" style={{ fontFamily: 'IBM Plex Sans,sans-serif' }}>
      <SEO title={`${t('knowledge.title')} | Durable Designs`} description={t('knowledge.articles_subtitle')} path="/knowledge" />

      <section className="relative py-20 px-4 sm:px-6 lg:px-8 overflow-hidden" style={{ background: '#06120B' }}>
        <div className="max-w-7xl mx-auto relative z-10">
          <h1 className="text-4xl sm:text-5xl font-extrabold text-white tracking-tighter mb-4" style={{ fontFamily: 'Plus Jakarta Sans,sans-serif' }} data-testid="knowledge-heading">
            {t('knowledge.title')}
          </h1>
        </div>
      </section>

      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
            <div data-testid="articles-content">
              <h2 className="text-2xl font-semibold text-gray-900 mb-2" style={{ fontFamily: 'Outfit,sans-serif' }}>{t('knowledge.articles_title')}</h2>
              <p className="text-base text-gray-500 mb-8">{t('knowledge.articles_subtitle')}</p>

              <div className="mb-10 border border-gray-200 bg-green-50 p-6 flex flex-col sm:flex-row items-start sm:items-center gap-4" data-testid="subscribe-section">
                <div className="flex items-center gap-3 flex-shrink-0">
                  <Mail className="w-5 h-5 text-green-700" />
                  <span className="text-sm font-medium text-gray-900">{t('knowledge.articles_subscribe')}</span>
                </div>
                {subStatus === 'success' ? (
                  <div className="flex items-center gap-2 text-sm text-green-700" data-testid="subscribe-success">
                    <CheckCircle className="w-4 h-4" /> Please check your email to confirm your subscription.
                  </div>
                ) : subStatus === 'already' ? (
                  <div className="flex items-center gap-2 text-sm text-green-700" data-testid="subscribe-already">
                    <CheckCircle className="w-4 h-4" /> This email is already subscribed.
                  </div>
                ) : (
                  <>
                    <form
                      className="flex flex-1 w-full sm:w-auto gap-3"
                      onSubmit={async (e) => {
                        e.preventDefault();
                        if (!subEmail) return;
                        setSubStatus('submitting');
                        try {
                          const res = await fetch(`${API_URL}/api/newsletter/subscribe`, {
                            method: 'POST',
                            headers: { 'Content-Type': 'application/json' },
                            body: JSON.stringify({ email: subEmail }),
                          });
                          if (res.ok) {
                            const data = await res.json();
                            setSubStatus(data.status === 'already_subscribed' ? 'already' : 'success');
                          } else if (res.status === 429) {
                            setSubStatus('error');
                          } else {
                            setSubStatus('error');
                          }
                        } catch {
                          setSubStatus('error');
                        }
                      }}
                      data-testid="subscribe-form"
                    >
                      <input
                        type="email"
                        value={subEmail}
                        onChange={(e) => setSubEmail(e.target.value)}
                        placeholder="your@email.com"
                        required
                        className="flex-1 px-4 py-2.5 border border-gray-200 text-sm text-gray-900 bg-white outline-none focus:border-green-500 transition-colors"
                        data-testid="subscribe-email-input"
                      />
                      <button
                        type="submit"
                        disabled={subStatus === 'submitting'}
                        className="bg-green-700 hover:bg-green-800 disabled:bg-gray-400 text-white px-5 py-2.5 text-sm font-medium tracking-wide transition-colors flex-shrink-0"
                        data-testid="subscribe-submit-btn"
                      >
                        {subStatus === 'submitting' ? 'Subscribing...' : t('knowledge.articles_subscribe')}
                      </button>
                    </form>
                    {subStatus === 'error' && (
                      <div className="flex items-center gap-2 text-sm text-red-600" data-testid="subscribe-error">
                        <AlertCircle className="w-4 h-4" /> Something went wrong. Please try again.
                      </div>
                    )}
                    <span className="text-xs text-gray-400">{t('knowledge.articles_nospam')}</span>
                  </>
                )}
              </div>

              <div className="flex gap-6 mb-10 border-b border-gray-100">
                {[
                  { key: 'materials', label: 'Materials' },
                  { key: 'risk', label: 'Risk' },
                  { key: 'digital', label: 'Digital & AI' },
                ].map(tab => (
                  <button key={tab.key} onClick={() => setArticleTab(tab.key)} data-testid={`article-tab-${tab.key}`}
                    className={`pb-3 text-sm font-medium border-b-2 transition-colors ${articleTab === tab.key ? 'border-green-600 text-gray-900' : 'border-transparent text-gray-400 hover:text-gray-600'}`}>
                    {tab.label}
                  </button>
                ))}
              </div>

              {articleTab === 'materials' && (
                <div className="grid md:grid-cols-2 gap-6 max-w-4xl" data-testid="articles-materials">
                  {ARTICLES.map((a, i) => (
                    <div key={i} className="article-card" data-testid={`article-card-${i}`}>
                      <span className="article-tag">{a.tag}</span>
                      <h3 className="article-title">{a.title}</h3>
                      <p className="article-excerpt">{a.excerpt}</p>
                      <a href={a.href} target="_blank" rel="noopener noreferrer" className="article-link inline-flex items-center gap-1.5" data-testid={`article-link-${i}`}>
                        Read on LinkedIn <ExternalLink className="w-3.5 h-3.5" />
                      </a>
                    </div>
                  ))}
                </div>
              )}

              {articleTab === 'risk' && (
                <div data-testid="articles-risk">
                  <p className="text-sm text-gray-400">Articles coming soon.</p>
                </div>
              )}

              {articleTab === 'digital' && (
                <div data-testid="articles-digital">
                  <p className="text-sm text-gray-400">Articles coming soon.</p>
                </div>
              )}
            </div>
        </div>
      </section>
    </div>
  );
};
export default Knowledge;
